import { useNavigate, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { NotificationBell } from "@/components/notifications/notification-bell";
import { useAuth } from "@/hooks/use-auth";
import { useAppMode } from "@/hooks/use-app-mode";
import { useNotifications } from "@/hooks/use-notifications";
import { ModeSelectionDialog } from "@/components/layout/mode-selection-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { ThemeToggle } from "@/components/ui/theme-toggle";
import {
  Building2,
  Clock,
  Users,
  CheckCircle2,
  MessageSquare,
  LayoutDashboard,
  LogOut,
  Settings,
  FileText,
  Activity,
  Key,
  MapPin,
  Wrench,
  CreditCard,
  ChevronDown,
  User,
  Bell,
  Shield,
  Sparkles,
  Palette
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Define types for the navigation items
interface NavItemChild {
  id: string;
  label: string;
  path: string;
}

interface NavItemBase {
  id: string;
  icon: LucideIcon;
  label: string;
}

interface NavItemWithPath extends NavItemBase {
  path: string;
  children?: never;
}

interface NavItemWithChildren extends NavItemBase {
  children: NavItemChild[];
  path?: never;
}

type NavItem = NavItemWithPath | NavItemWithChildren;

// Type guard to check if an item has children
function isNavItemWithChildren(item: NavItem): item is NavItemWithChildren {
  return 'children' in item && Array.isArray((item as NavItemWithChildren).children);
}

// Type guard to check if an item has a path
function isNavItemWithPath(item: NavItem): item is NavItemWithPath {
  return 'path' in item && typeof (item as NavItemWithPath).path === 'string';
}

const adminNavItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
  { id: 'users', icon: Users, label: 'Users', path: '/admin/users' },
  { id: 'contracts', icon: FileText, label: 'Contracts', path: '/admin/contracts' },
  { id: 'chat', icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'flexhelp-ai', icon: Sparkles, label: 'FlexHelp AI', path: '/flexhelp-ai' },
  { id: 'settings', icon: Settings, label: 'Settings', path: '/admin/settings' },
  { id: 'audit', icon: Activity, label: 'Audit Logs', path: '/admin/audit' },
  { id: 'api', icon: Key, label: 'API Keys', path: '/admin/api-keys' },
];

const propertyManagerNavItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/manager/dashboard' },
  { 
    id: 'service-requests', 
    icon: Wrench, 
    label: 'Service Requests', 
    children: [
      { id: 'requests', label: 'Service Requests', path: '/manager/service-requests' },
      { id: 'tasks', label: 'Task Management', path: '/manager/service-requests/tasks' }
    ]
  },
  { id: 'properties', icon: Building2, label: 'Properties', path: '/manager/properties' },
  { id: 'helpers', icon: Users, label: 'FlexHelpers', path: '/manager/helpers' },
  { id: 'chat', icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'flexhelp-ai', icon: Sparkles, label: 'FlexHelp AI', path: '/flexhelp-ai' },
];

const propertyOwnerNavItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/owner/dashboard' },
  { 
    id: 'service-requests', 
    icon: Wrench, 
    label: 'Service Requests', 
    children: [
      { id: 'requests', label: 'Service Requests', path: '/owner/service-requests' },
      { id: 'tasks', label: 'Task Management', path: '/owner/service-requests/tasks' }
    ]
  },
  { id: 'chat', icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'flexhelp-ai', icon: Sparkles, label: 'FlexHelp AI', path: '/flexhelp-ai' },
  { id: 'feedback', icon: MessageSquare, label: 'Feedback', path: '/owner/feedback' },
  { id: 'portal', icon: CreditCard, label: 'Customer Portal', path: '/owner/customer-portal' },
];

const flexHelperNavItems = [
  { id: 'dashboard', icon: LayoutDashboard, label: 'Dashboard', path: '/helper/dashboard' },
  { id: 'chat', icon: MessageSquare, label: 'Chat', path: '/chat' },
  { id: 'flexhelp-ai', icon: Sparkles, label: 'FlexHelp AI', path: '/flexhelp-ai' },
  { id: 'location', icon: MapPin, label: 'Location', path: '/helper/location' },
  { id: 'feedback', icon: MessageSquare, label: 'Feedback', path: '/helper/feedback' },
];

interface DashboardLayoutProps {
  children: React.ReactNode;
}

export function DashboardLayout({ children }: DashboardLayoutProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const { mode, setMode } = useAppMode();
  const { 
    notifications, 
    handleNotificationClick, 
    clearAll 
  } = useNotifications();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const getRoleIcon = () => {
    switch (user?.role) {
      case 'admin':
        return Shield;
      case 'property_manager':
        return Building2;
      case 'property_owner':
        return Key;
      case 'flex_helper':
        return Wrench;
      default:
        return User;
    }
  };

  const RoleIcon = getRoleIcon();

  const getNavItems = () => {
    switch (user?.role) {
      case 'admin':
        return adminNavItems;
      case 'property_manager':
        return propertyManagerNavItems;
      case 'property_owner':
        return propertyOwnerNavItems;
      case 'flex_helper':
        return flexHelperNavItems;
      default:
        return [];
    }
  };

  const navItems = getNavItems();

  const renderNavItem = (item: NavItem) => {
    // Special handling for FlexHelp AI navigation
    const handleNavigation = (path: string) => {
      // If navigating to FlexHelp AI, set the mode to AI
      if (item.id === 'flexhelp-ai') {
        setMode('ai');
      }
      navigate(path);
    };

    if (isNavItemWithChildren(item)) {
      return (
        <DropdownMenu key={item.id}>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              className={cn(
                'w-full justify-between gap-3',
                location.pathname.startsWith(item.children[0].path.split('/').slice(0, -1).join('/'))
                  ? 'bg-primary/10 text-primary hover:bg-primary/20'
                  : 'text-muted-foreground hover:text-foreground hover:bg-primary/5',
                'h-11 rounded-lg px-4'
              )}
            >
              <div className="flex items-center gap-3">
                <item.icon className="h-5 w-5" />
                <span className="font-medium">{item.label}</span>
              </div>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent side="right" className="w-56">
            {item.children.map((child) => (
              <DropdownMenuItem
                key={child.id}
                className="cursor-pointer"
                onClick={() => handleNavigation(child.path)}
              >
                {child.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      );
    }

    return (
      <Button
        key={item.id}
        variant="ghost"
        className={cn(
          'w-full justify-start gap-3',
          location.pathname === item.path
            ? 'bg-primary/10 text-primary hover:bg-primary/20'
            : 'text-muted-foreground hover:text-foreground hover:bg-primary/5',
          'h-11 rounded-lg px-4'
        )}
        onClick={() => handleNavigation(item.path)}
      >
        <item.icon className="h-5 w-5" />
        <span className="font-medium">{item.label}</span>
      </Button>
    );
  };

  return (
    <div className="flex h-screen overflow-hidden bg-background">
      {/* Mode Selection Dialog - shows after login */}
      <ModeSelectionDialog />
      
      {/* Sidebar */}
      <aside className="hidden w-64 flex-col border-r bg-card/50 backdrop-blur-sm md:flex shadow-sm">
        <div className="flex h-32 items-center border-b px-6">
          <img 
            src="/logo.svg" 
            alt="FlexHelp" 
            className="h-48 w-auto logo-image"
          />
        </div>
        <nav className="flex-1 space-y-1 p-4 overflow-y-auto">
          {navItems.map(renderNavItem)}
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex flex-1 flex-col overflow-hidden">
        {/* Header */}
        <header className="sticky top-0 z-10 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 shadow-sm">
          <div className="flex h-16 items-center justify-between px-6">
            <h1 className="text-xl font-semibold text-primary-700 dark:text-primary-300">
              {navItems.find((item: NavItem) => {
                if (isNavItemWithChildren(item)) {
                  return item.children.some(child => child.path === location.pathname);
                }
                return isNavItemWithPath(item) && item.path === location.pathname;
              })?.label || 'Dashboard'}
            </h1>
            <div className="flex items-center gap-4">
              <ThemeToggle />
              {/* Mode Selector Toggle */}
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      onClick={() => {
                        // Toggle between AI and traditional mode
                        if (mode === 'ai') {
                          setMode('traditional');
                          navigate('/chat');
                        } else {
                          setMode('ai');
                          navigate('/flexhelp-ai');
                        }
                      }}
                    >
                      {mode === 'ai' ? (
                        <Sparkles className="h-4 w-4 text-primary" />
                      ) : (
                        <MessageSquare className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {mode === 'ai' ? 'Switch to Traditional Mode' : 'Switch to AI Mode'}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <NotificationBell
                notifications={notifications}
                onNotificationClick={handleNotificationClick}
                onClearAll={clearAll}
              />
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="gap-2 rounded-full">
                    <Avatar variant="bordered" className="h-8 w-8">
                      <AvatarImage src={user?.avatar} />
                      <AvatarFallback className="bg-primary/10">
                        {user?.name?.[0]}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex items-center gap-2">
                      <div className="text-start">
                        <p className="text-sm font-medium leading-none">{user?.name}</p>
                        <p className="text-xs text-muted-foreground capitalize">
                          {user?.role.replace('_', ' ')}
                        </p>
                      </div>
                      <ChevronDown className="h-4 w-4 text-muted-foreground" />
                    </div>
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="flex items-center gap-2">
                    <RoleIcon className="h-4 w-4 text-primary" />
                    <span>Account</span>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate('/profile')}>
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/settings')}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/notifications')}>
                    <Bell className="mr-2 h-4 w-4" />
                    Notifications
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={handleLogout}
                  >
                    <LogOut className="mr-2 h-4 w-4" />
                    Log out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Scrollable Content with subtle grid background */}
        <main className="flex-1 overflow-y-auto relative">
          {/* Background pattern */}
          <div className="absolute inset-0 -z-10 bg-grid-black/5 dark:bg-grid-white/5">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-secondary/5"></div>
          </div>
          <div className="container max-w-6xl py-6">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}